import React, { useState, useEffect } from 'react';
import { format } from 'date-fns';
import { 
  Search, Check, X, Clock, ChevronDown, ChevronUp,
  Loader2, Upload, Trash2, AlertCircle, Filter, Calendar,
  Phone, PoundSterling, User, Mail
} from 'lucide-react';
import AdminLayout from '../components/admin/AdminLayout';
import { 
  fetchBookings, updateBookingStatus, fetchCategories, 
  fetchServices, API_URL 
} from '../services/api';

// Confirmation Modal Component
const ConfirmationModal: React.FC<{
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  message: string;
  isLoading?: boolean;
}> = ({ isOpen, onClose, onConfirm, title, message, isLoading }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-sm w-full mx-4">
        <h3 className="text-xl font-bold mb-4">{title}</h3>
        <p className="text-gray-600 dark:text-gray-400 mb-6">{message}</p>
        <div className="flex justify-end space-x-4">
          <button
            onClick={onClose}
            disabled={isLoading}
            className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={isLoading}
            className="px-4 py-2 bg-error-600 text-white rounded-lg hover:bg-error-700 disabled:opacity-50 flex items-center"
          >
            {isLoading ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Deleting...
              </>
            ) : (
              'Delete'
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

// Booking Modal Component
const BookingModal: React.FC<{
  booking: any;
  onClose: () => void;
  onStatusUpdate: (id: string, status: string) => Promise<void>;
}> = ({ booking, onClose, onStatusUpdate }) => {
  const [loading, setLoading] = useState<string | null>(null);

  const handleStatusUpdate = async (status: string) => {
    setLoading(status);
    try {
      await onStatusUpdate(booking.id.toString(), status);
      onClose();
    } catch (error) {
      console.error('Error updating status:', error);
    } finally {
      setLoading(null);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending': return 'bg-warning-100 text-warning-800 dark:bg-warning-900/30 dark:text-warning-300';
      case 'confirmed': return 'bg-success-100 text-success-800 dark:bg-success-900/30 dark:text-success-300';
      case 'completed': return 'bg-primary-100 text-primary-800 dark:bg-primary-900/30 dark:text-primary-300';
      case 'cancelled':
      case 'rejected': return 'bg-error-100 text-error-800 dark:bg-error-900/30 dark:text-error-300';
      default: return 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-300';
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-bold">Booking Details</h2>
            <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(booking.status)}`}>
              {booking.status.charAt(0).toUpperCase() + booking.status.slice(1)}
            </span>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            <div>
              <h3 className="font-medium mb-4">Customer Information</h3>
              <div className="space-y-3">
                <div className="flex items-center">
                  <User className="w-4 h-4 mr-2 text-gray-500" />
                  <span className="text-sm text-gray-600 dark:text-gray-400 mr-2">Name:</span>
                  <span className="font-medium">{booking.customer_name}</span>
                </div>
                <div className="flex items-center">
                  <Mail className="w-4 h-4 mr-2 text-gray-500" />
                  <span className="text-sm text-gray-600 dark:text-gray-400 mr-2">Email:</span>
                  <span className="font-medium">{booking.customer_email}</span>
                </div>
                <div className="flex items-center">
                  <Phone className="w-4 h-4 mr-2 text-gray-500" />
                  <span className="text-sm text-gray-600 dark:text-gray-400 mr-2">Phone:</span>
                  <span className="font-medium">{booking.customer_phone}</span>
                </div>
              </div>
            </div>
            
            <div>
              <h3 className="font-medium mb-4">Booking Information</h3>
              <div className="space-y-3">
                <div className="flex items-center">
                  <Calendar className="w-4 h-4 mr-2 text-gray-500" />
                  <span className="text-sm text-gray-600 dark:text-gray-400 mr-2">Date & Time:</span>
                  <span className="font-medium">{booking.booking_date_formatted}</span>
                </div>
                <div className="flex items-center">
                  <PoundSterling className="w-4 h-4 mr-2 text-gray-500" />
                  <span className="text-sm text-gray-600 dark:text-gray-400 mr-2">Service Amount:</span>
                  <span className="font-medium">£{booking.service_amount || 'N/A'}</span>
                </div>
                <div className="flex items-center">
                  <PoundSterling className="w-4 h-4 mr-2 text-gray-500" />
                  <span className="text-sm text-gray-600 dark:text-gray-400 mr-2">Deposit:</span>
                  <span className="font-medium">£{booking.deposit_amount}</span>
                </div>
              </div>
            </div>
          </div>

          <div className="mb-6">
            <h3 className="font-medium mb-2">Service Details</h3>
            <p className="text-gray-600 dark:text-gray-400">
              <span className="font-medium">Service:</span> {booking.service_name || 'N/A'}
            </p>
            {booking.notes && (
              <div className="mt-2">
                <span className="font-medium">Notes:</span>
                <p className="text-gray-600 dark:text-gray-400 mt-1">{booking.notes}</p>
              </div>
            )}
          </div>

          <div className="mb-6">
            <h3 className="font-medium mb-2">Payment Receipt</h3>
            <img 
              src={booking.payment_receipt_url}
              alt="Payment Receipt"
              className="max-w-full h-auto rounded-lg border"
            />
          </div>

          <div className="flex flex-wrap gap-4 justify-end">
            <button
              onClick={() => handleStatusUpdate('rejected')}
              disabled={loading !== null}
              className="px-4 py-2 bg-error-600 text-white rounded-lg hover:bg-error-700 disabled:opacity-50 flex items-center"
            >
              {loading === 'rejected' ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Rejecting...</>
              ) : (
                <><X className="w-4 h-4 mr-2" /> Reject</>
              )}
            </button>
            
            <button
              onClick={() => handleStatusUpdate('confirmed')}
              disabled={loading !== null}
              className="px-4 py-2 bg-success-600 text-white rounded-lg hover:bg-success-700 disabled:opacity-50 flex items-center"
            >
              {loading === 'confirmed' ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Confirming...</>
              ) : (
                <><Check className="w-4 h-4 mr-2" /> Confirm</>
              )}
            </button>
            
            <button
              onClick={() => handleStatusUpdate('completed')}
              disabled={loading !== null}
              className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 flex items-center"
            >
              {loading === 'completed' ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Completing...</>
              ) : (
                <><Clock className="w-4 h-4 mr-2" /> Mark as Completed</>
              )}
            </button>
            
            <button
              onClick={onClose}
              disabled={loading !== null}
              className="px-4 py-2 bg-gray-200 dark:bg-gray-700 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Style Form Modal Component
const StyleFormModal: React.FC<{
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: any) => Promise<void>;
  categories: any[];
  style?: any;
}> = ({ isOpen, onClose, onSubmit, categories, style }) => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    price: '',
    hours_min: 1,
    hours_max: 2,
    category_id: ''
  });
  const [image, setImage] = useState<File | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Reset state when modal opens or style changes
  useEffect(() => {
    if (isOpen) {
      setFormData({
        name: style?.name || '',
        description: style?.description || '',
        price: style?.amount || style?.price || '',
        hours_min: style?.hours_min || 1,
        hours_max: style?.hours_max || 2,
        category_id: style?.category_id ? String(style.category_id) : ''
      });
      setImage(null);
      setError(null);
    }
  }, [isOpen, style]);

  if (!isOpen) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.name.trim()) {
      setError('Style name is required');
      return;
    }
    
    if (!formData.price) {
      setError('Price is required');
      return;
    }
    
    if (!formData.category_id) {
      setError('Category is required');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const imageBase64 = image ? await convertFileToBase64(image) : undefined;
      await onSubmit({
        name: formData.name,
        description: formData.description,
        amount: formData.price,
        categoryId: formData.category_id,
        hours_min: formData.hours_min,
        hours_max: formData.hours_max,
        image: imageBase64
      });
      onClose();
    } catch (err) {
      setError('Failed to save style');
    } finally {
      setLoading(false);
    }
  };

  const convertFileToBase64 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto mx-4">
        <h2 className="text-xl font-bold mb-4">
          {style ? 'Edit Style' : 'Add New Style'}
        </h2>

        <form onSubmit={handleSubmit}>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-1">Name</label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="w-full px-3 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600"
                placeholder="Enter style name"
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">Description</label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                className="w-full px-3 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600"
                rows={3}
                placeholder="Enter style description"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1">Price (£)</label>
                <input
                  type="number"
                  value={formData.price}
                  onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600"
                  placeholder="Enter price"
                  min="0"
                  step="0.01"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Category</label>
                <select
                  value={formData.category_id}
                  onChange={(e) => setFormData({ ...formData, category_id: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600"
                >
                  <option value="">Select category</option>
                  {categories.map((category) => (
                    <option key={category.id} value={category.id}>
                      {category.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1">Min Hours</label>
                <input
                  type="number"
                  value={formData.hours_min}
                  onChange={(e) => setFormData({ ...formData, hours_min: parseInt(e.target.value) })}
                  className="w-full px-3 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600"
                  min="1"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Max Hours</label>
                <input
                  type="number"
                  value={formData.hours_max}
                  onChange={(e) => setFormData({ ...formData, hours_max: parseInt(e.target.value) })}
                  className="w-full px-3 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600"
                  min="1"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">Image</label>
              <div className="border-2 border-dashed rounded-lg p-4 text-center">
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => setImage(e.target.files?.[0] || null)}
                  className="hidden"
                  id="style-image"
                />
                
                {/* Show existing image when editing */}
                {style?.image && !image && (
                  <div className="mb-4">
                    <p className="text-sm text-gray-500 mb-2">Current image:</p>
                    <img
                      src={style.image}
                      alt={style.name}
                      className="w-32 h-32 object-cover rounded-lg mx-auto"
                    />
                  </div>
                )}
                
                {/* Show preview when new image selected */}
                {image && (
                  <div className="mb-4">
                    <p className="text-sm text-green-600 mb-2">New image selected:</p>
                    <img
                      src={URL.createObjectURL(image)}
                      alt="Preview"
                      className="w-32 h-32 object-cover rounded-lg mx-auto"
                    />
                    <p className="text-sm text-gray-600 mt-2">{image.name}</p>
                    <button
                      type="button"
                      onClick={() => setImage(null)}
                      className="text-sm text-red-600 mt-2 hover:underline"
                    >
                      Remove
                    </button>
                  </div>
                )}
                
                {/* Upload area - hide when image selected */}
                {!image && (
                  <label
                    htmlFor="style-image"
                    className="cursor-pointer flex flex-col items-center"
                  >
                    <Upload className="w-8 h-8 mb-2 text-gray-400" />
                    <span className="text-sm text-gray-600 dark:text-gray-400">
                      {style?.image ? 'Click to change image' : 'Click to upload or drag and drop'}
                    </span>
                    <span className="text-xs text-gray-500">
                      WEBP, PNG, JPG (will be optimized to under 300KB)
                    </span>
                  </label>
                )}
              </div>
            </div>
          </div>

          {error && (
            <div className="mt-4 flex items-start space-x-2 text-error-600">
              <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
              <span className="text-sm">{error}</span>
            </div>
          )}

          <div className="flex justify-end space-x-4 mt-6">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg dark:text-gray-400 dark:hover:bg-gray-700"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 flex items-center"
            >
              {loading ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving...</>
              ) : (
                'Save'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Category Form Modal Component
const CategoryFormModal: React.FC<{
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: any) => Promise<void>;
  category?: any;
}> = ({ isOpen, onClose, onSubmit, category }) => {
  const [name, setName] = useState(category?.name || '');
  const [image, setImage] = useState<File | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Reset state when modal opens or category changes
  useEffect(() => {
    if (isOpen) {
      setName(category?.name || '');
      setImage(null);
      setError(null);
    }
  }, [isOpen, category]);

  if (!isOpen) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!name.trim()) {
      setError('Category name is required');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      
      const imageBase64 = image ? await convertFileToBase64(image) : undefined;
      
      await onSubmit({
        name,
        image: imageBase64
      });
      onClose();
    } catch (err) {
      setError('Failed to save category');
    } finally {
      setLoading(false);
    }
  };

  const convertFileToBase64 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-md mx-4">
        <h2 className="text-xl font-bold mb-4">
          {category ? 'Edit Category' : 'Add New Category'}
        </h2>

        <form onSubmit={handleSubmit}>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-1">Name</label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="w-full px-3 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600"
                placeholder="Enter category name"
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">Image</label>
              <div className="border-2 border-dashed rounded-lg p-4 text-center">
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => setImage(e.target.files?.[0] || null)}
                  className="hidden"
                  id="category-image"
                />
                
                {/* Show existing image when editing */}
                {category?.image && !image && (
                  <div className="mb-4">
                    <p className="text-sm text-gray-500 mb-2">Current image:</p>
                    <img
                      src={category.image}
                      alt={category.name}
                      className="w-32 h-32 object-cover rounded-lg mx-auto"
                    />
                  </div>
                )}
                
                {/* Show preview when new image selected */}
                {image && (
                  <div className="mb-4">
                    <p className="text-sm text-green-600 mb-2">New image selected:</p>
                    <img
                      src={URL.createObjectURL(image)}
                      alt="Preview"
                      className="w-32 h-32 object-cover rounded-lg mx-auto"
                    />
                    <p className="text-sm text-gray-600 mt-2">{image.name}</p>
                    <button
                      type="button"
                      onClick={() => setImage(null)}
                      className="text-sm text-red-600 mt-2 hover:underline"
                    >
                      Remove
                    </button>
                  </div>
                )}
                
                {/* Upload area - hide when image selected */}
                {!image && (
                  <label
                    htmlFor="category-image"
                    className="cursor-pointer flex flex-col items-center"
                  >
                    <Upload className="w-8 h-8 mb-2 text-gray-400" />
                    <span className="text-sm text-gray-600 dark:text-gray-400">
                      {category?.image ? 'Click to change image' : 'Click to upload or drag and drop'}
                    </span>
                    <span className="text-xs text-gray-500">
                      WEBP, PNG, JPG (will be optimized to under 300KB)
                    </span>
                  </label>
                )}
              </div>
            </div>
          </div>

          {error && (
            <div className="mt-4 flex items-start space-x-2 text-error-600">
              <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
              <span className="text-sm">{error}</span>
            </div>
          )}

          <div className="flex justify-end space-x-4 mt-6">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg dark:text-gray-400 dark:hover:bg-gray-700"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 flex items-center"
            >
              {loading ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving...</>
              ) : (
                'Save'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Main Dashboard Component
const AdminDashboardPage = () => {
  const [expandedSection, setExpandedSection] = useState<string>('bookings');
  const [allBookings, setAllBookings] = useState([]);
  const [filteredBookings, setFilteredBookings] = useState([]);
  const [categories, setCategories] = useState([]);
  const [services, setServices] = useState([]);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [showStyleModal, setShowStyleModal] = useState(false);
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [selectedStyle, setSelectedStyle] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState<{
    type: 'style' | 'category';
    id: number;
    name: string;
  } | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [imageCacheBuster, setImageCacheBuster] = useState(Date.now());

  const itemsPerPage = 10;

  useEffect(() => {
    loadData();
  }, [expandedSection]);

  useEffect(() => {
    filterBookings();
  }, [allBookings, search, statusFilter, page]);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);

      if (expandedSection === 'bookings') {
        const data = await fetchBookings(1, '', 'booking_date', 'desc');
        setAllBookings(data.bookings || []);
        setTotalPages(Math.ceil((data.bookings || []).length / itemsPerPage));
      } else if (expandedSection === 'styles' || expandedSection === 'categories') {
        const [categoriesData, servicesData] = await Promise.all([
          fetchCategories(),
          fetchServices()
        ]);
        setCategories(categoriesData);
        setServices(servicesData);
      }
    } catch (err) {
      setError('Failed to load data');
      console.error('Error loading data:', err);
    } finally {
      setLoading(false);
    }
  };

  const filterBookings = () => {
    let filtered = [...allBookings];

    // Sort by status priority (pending first)
    filtered.sort((a, b) => {
      const statusPriority = { pending: 0, confirmed: 1, completed: 2, rejected: 3 };
      return statusPriority[a.status] - statusPriority[b.status];
    });

    // Filter by search
    if (search.trim()) {
      const searchLower = search.toLowerCase();
      filtered = filtered.filter(booking => 
        booking.customer_name.toLowerCase().includes(searchLower) ||
        booking.customer_email.toLowerCase().includes(searchLower) ||
        booking.customer_phone.includes(search) ||
        (booking.service_name && booking.service_name.toLowerCase().includes(searchLower))
      );
    }

    // Filter by status
    if (statusFilter !== 'all') {
      filtered = filtered.filter(booking => booking.status === statusFilter);
    }

    // Pagination
    const startIndex = (page - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    const paginatedBookings = filtered.slice(startIndex, endIndex);

    setFilteredBookings(paginatedBookings);
    setTotalPages(Math.ceil(filtered.length / itemsPerPage));
  };

  const handleStatusUpdate = async (bookingId: string, status: string) => {
    try {
      await updateBookingStatus(bookingId, status);
      await loadData();
    } catch (err) {
      console.error('Error updating booking status:', err);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending': return 'bg-warning-100 text-warning-800 dark:bg-warning-900/30 dark:text-warning-300';
      case 'confirmed': return 'bg-success-100 text-success-800 dark:bg-success-900/30 dark:text-success-300';
      case 'completed': return 'bg-primary-100 text-primary-800 dark:bg-primary-900/30 dark:text-primary-300';
      case 'cancelled':
      case 'rejected': return 'bg-error-100 text-error-800 dark:bg-error-900/30 dark:text-error-300';
      default: return 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-300';
    }
  };

  const handleStyleSubmit = async (data: any) => {
    console.log('Style submit - selectedStyle:', selectedStyle);
    console.log('Style submit - selectedStyle?.id:', selectedStyle?.id);
    try {
      setIsSaving(true);
      const payload = {
        action: selectedStyle?.id ? 'updateService' : 'addService',
        storeId: '372',
        ...(selectedStyle?.id && { serviceId: selectedStyle.id }),
        ...data
      };
      console.log('Style submit payload:', payload);
      const response = await fetch(API_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload)
      });

      const result = await response.json();
      
      if (result.success) {
        setImageCacheBuster(Date.now()); // Force image reload
        await loadData();
      } else {
        throw new Error(result.message);
      }
    } catch (err) {
      console.error('Error saving style:', err);
      throw err;
    } finally {
      setIsSaving(false);
    }
  };

  const handleCategorySubmit = async (data: any) => {
    try {
      setIsSaving(true);
      const isEditing = selectedCategory?.id;
      const response = await fetch(API_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          action: isEditing ? 'updateCategory' : 'addCategory',
          storeId: '372',
          ...(isEditing && { categoryId: selectedCategory.id }),
          ...data
        })
      });

      const result = await response.json();
      
      if (result.success) {
        await loadData();
      } else {
        throw new Error(result.message);
      }
    } catch (err) {
      console.error('Error saving category:', err);
      throw err;
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!showDeleteConfirm) return;

    try {
      setIsDeleting(true);
      const response = await fetch(API_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          action: showDeleteConfirm.type === 'style' ? 'deleteService' : 'deleteCategory',
          storeId: '372',
          [showDeleteConfirm.type === 'style' ? 'serviceId' : 'categoryId']: showDeleteConfirm.id
        })
      });

      const result = await response.json();
      
      if (result.success) {
        await loadData();
        setShowDeleteConfirm(null);
      } else {
        throw new Error(result.message);
      }
    } catch (err) {
      console.error('Error deleting item:', err);
    } finally {
      setIsDeleting(false);
    }
  };



  return (
    <AdminLayout>
      <div className="max-w-6xl mx-auto">
        <h1 className="text-2xl font-bold mb-6">Booking Management</h1>

        {/* Bookings Section */}
        <div className="mb-4">
          <button
            onClick={() => setExpandedSection(expandedSection === 'bookings' ? '' : 'bookings')}
            className="w-full flex items-center justify-between p-4 bg-white dark:bg-gray-800 rounded-lg shadow"
          >
            <span className="text-lg font-medium">Bookings</span>
            {expandedSection === 'bookings' ? (
              <ChevronUp className="w-5 h-5" />
            ) : (
              <ChevronDown className="w-5 h-5" />
            )}
          </button>

          {expandedSection === 'bookings' && (
            <div className="mt-4 bg-white dark:bg-gray-800 rounded-lg shadow p-4">
              <div className="flex flex-col md:flex-row gap-4 mb-6">
                <div className="flex-1 relative">
                  <input
                    type="text"
                    placeholder="Search bookings..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 rounded-lg border dark:bg-gray-700 dark:border-gray-600"
                  />
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                </div>
                
                <div className="relative">
                  <select
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    className="pl-10 pr-8 py-2 rounded-lg border dark:bg-gray-700 dark:border-gray-600 appearance-none"
                  >
                    <option value="all">All Status</option>
                    <option value="pending">Pending</option>
                    <option value="confirmed">Confirmed</option>
                    <option value="completed">Completed</option>
                    <option value="rejected">Rejected</option>
                  </select>
                  <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                </div>
              </div>

              {loading ? (
                <div className="flex justify-center py-8">
                  <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
                </div>
              ) : error ? (
                <div className="text-center py-8 text-error-600">
                  {error}
                </div>
              ) : filteredBookings.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  No bookings found
                </div>
              ) : (
                <>
                  <div className="space-y-3">
                    {filteredBookings.map((booking: any) => (
                      <div
                        key={booking.id}
                        onClick={() => setSelectedBooking(booking)}
                        className="p-4 bg-gray-50 dark:bg-gray-700 rounded-lg cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors"
                      >
                        <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
                          <div className="flex-1">
                            <div className="flex items-center gap-3 mb-2">
                              <h3 className="font-medium text-lg">{booking.customer_name}</h3>
                              <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(booking.status)}`}>
                                {booking.status.charAt(0).toUpperCase() + booking.status.slice(1)}
                              </span>
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-2 text-sm text-gray-600 dark:text-gray-400">
                              <div className="flex items-center">
                                <Calendar className="w-4 h-4 mr-1" />
                                {booking.booking_date_formatted}
                              </div>
                              <div className="flex items-center">
                                <Phone className="w-4 h-4 mr-1" />
                                {booking.customer_phone}
                              </div>
                              <div className="flex items-center">
                                <PoundSterling className="w-4 h-4 mr-1" />
                                £{booking.service_amount || 'N/A'} (Deposit: £{booking.deposit_amount})
                              </div>
                            </div>
                            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                              {booking.service_name || 'Service not specified'}
                            </p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Pagination */}
                  {totalPages > 1 && (
                    <div className="flex justify-center items-center gap-2 mt-6">
                      <button
                        onClick={() => setPage(Math.max(1, page - 1))}
                        disabled={page === 1}
                        className="px-3 py-1 rounded border disabled:opacity-50"
                      >
                        Previous
                      </button>
                      <span className="px-3 py-1">
                        Page {page} of {totalPages}
                      </span>
                      <button
                        onClick={() => setPage(Math.min(totalPages, page + 1))}
                        disabled={page === totalPages}
                        className="px-3 py-1 rounded border disabled:opacity-50"
                      >
                        Next
                      </button>
                    </div>
                  )}
                </>
              )}
            </div>
          )}
        </div>

        {/* Styles Section */}
        <div className="mb-4">
          <button
            onClick={() => setExpandedSection(expandedSection === 'styles' ? '' : 'styles')}
            className="w-full flex items-center justify-between p-4 bg-white dark:bg-gray-800 rounded-lg shadow"
          >
            <span className="text-lg font-medium">Styles</span>
            {expandedSection === 'styles' ? (
              <ChevronUp className="w-5 h-5" />
            ) : (
              <ChevronDown className="w-5 h-5" />
            )}
          </button>

          {expandedSection === 'styles' && (
            <div className="mt-4 bg-white dark:bg-gray-800 rounded-lg shadow p-4">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-medium">Styles Management</h3>
                <button
                  onClick={() => {
                    setSelectedStyle(null);
                    setShowStyleModal(true);
                  }}
                  className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
                >
                  Add Style
                </button>
              </div>

              {loading ? (
                <div className="flex justify-center py-8">
                  <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
                </div>
              ) : (
                <div className="grid gap-4">
                  {services.map((style: any) => (
                    <div
                      key={style.id}
                      className="p-4 bg-gray-50 dark:bg-gray-700 rounded-lg flex items-center justify-between"
                    >
                      <div className="flex items-center">
                        <img
                          src={style.image ? `${style.image}?v=${imageCacheBuster}` : '/img/placeholder.svg'}
                          alt={style.name}
                          className="w-16 h-16 rounded-lg object-cover mr-4"
                          onError={(e) => {
                            const target = e.target as HTMLImageElement;
                            target.src = '/img/placeholder.svg';
                          }}
                        />
                        <div>
                          <h4 className="font-medium">{style.name}</h4>
                          <p className="text-sm text-gray-600 dark:text-gray-400">
                            {style.hours_min}-{style.hours_max} hrs • £{style.amount}
                          </p>
                        </div>
                      </div>
                      <div className="flex space-x-2">
                        <button
                          onClick={() => {
                            setSelectedStyle(style);
                            setShowStyleModal(true);
                          }}
                          className="p-2 text-primary-600 hover:bg-primary-50 rounded-lg"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => setShowDeleteConfirm({
                            type: 'style',
                            id: style.id,
                            name: style.name
                          })}
                          className="p-2 text-error-600 hover:bg-error-50 rounded-lg"
                        >
                          <Trash2 className="w-5 h-5" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Categories Section */}
        <div className="mb-4">
          <button
            onClick={() => setExpandedSection(expandedSection === 'categories' ? '' : 'categories')}
            className="w-full flex items-center justify-between p-4 bg-white dark:bg-gray-800 rounded-lg shadow"
          >
            <span className="text-lg font-medium">Categories</span>
            {expandedSection === 'categories' ? (
              <ChevronUp className="w-5 h-5" />
            ) : (
              <ChevronDown className="w-5 h-5" />
            )}
          </button>

          {expandedSection === 'categories' && (
            <div className="mt-4 bg-white dark:bg-gray-800 rounded-lg shadow p-4">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-medium">Category Management</h3>
                <button
                  onClick={() => {
                    setSelectedCategory(null);
                    setShowCategoryModal(true);
                  }}
                  className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
                >
                  Add Category
                </button>
              </div>

              {loading ? (
                <div className="flex justify-center py-8">
                  <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
                </div>
              ) : (
                <div className="grid gap-4">
                  {categories.map((category: unknown) => (
                    <div
                      key={category.id}
                      className="p-4 bg-gray-50 dark:bg-gray-700 rounded-lg flex items-center justify-between"
                    >
                      <div className="flex items-center">
                        <img
                          src={category.image || '/img/placeholder.svg'}
                          alt={category.name}
                          className="w-16 h-16 rounded-lg object-cover mr-4"
                          onError={(e) => {
                            const target = e.target as HTMLImageElement;
                            target.src = '/img/placeholder.svg';
                          }}
                        />
                        <h4 className="font-medium">{category.name}</h4>
                      </div>
                      <div className="flex space-x-2">
                        <button
                          onClick={() => {
                            setSelectedCategory(category);
                            setShowCategoryModal(true);
                          }}
                          className="p-2 text-primary-600 hover:bg-primary-50 rounded-lg"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => setShowDeleteConfirm({
                            type: 'category',
                            id: category.id,
                            name: category.name
                          })}
                          className="p-2 text-error-600 hover:bg-error-50 rounded-lg"
                        >
                          <Trash2 className="w-5 h-5" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>



        {/* Modals */}
        {selectedBooking && (
          <BookingModal
            booking={selectedBooking}
            onClose={() => setSelectedBooking(null)}
            onStatusUpdate={handleStatusUpdate}
          />
        )}

        {showStyleModal && (
          <StyleFormModal
            isOpen={showStyleModal}
            onClose={() => setShowStyleModal(false)}
            onSubmit={handleStyleSubmit}
            categories={categories}
            style={selectedStyle}
          />
        )}

        {showCategoryModal && (
          <CategoryFormModal
            isOpen={showCategoryModal}
            onClose={() => setShowCategoryModal(false)}
            onSubmit={handleCategorySubmit}
            category={selectedCategory}
          />
        )}

        {showDeleteConfirm && (
          <ConfirmationModal
            isOpen={true}
            onClose={() => setShowDeleteConfirm(null)}
            onConfirm={handleDelete}
            title={`Delete ${showDeleteConfirm.type === 'style' ? 'Style' : 'Category'}`}
            message={`Are you sure you want to delete "${showDeleteConfirm.name}"? This action cannot be undone.`}
            isLoading={isDeleting}
          />
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminDashboardPage;